'use client'

import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { DialogContentSkeleton } from '@/features/dialogs/common/components/skeletons/DialogContentSkeleton'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TabSelector } from '@/ui/atoms/tab-selector/TabSelector'
import { withSuspense } from '@/ui/utils/withSuspense'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Address } from 'viem'
import { EBorrowRepayWithCollateralView } from './views/EBorrowRepayWithCollateralView'
import { EvcRepayDialogContent } from './views/EvcRepayDialogContent'

/** Collateral option for repay with collateral */
export interface CollateralOption {
  vaultAddress: Address
  symbol: string
  decimals: number
  assetAddress: Address
  logoURI?: string
  balance: string // Formatted balance
  shares?: string
}

export interface EBorrowUnifiedRepayDialogProps extends CommonDialogProps {
  // Liability (debt) info
  liabilityVault: Address
  liabilitySymbol: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  maxRepayAmount: string // Total debt owed
  // Collateral info - can be single (backwards compat) or array of options
  collateralVault?: Address
  collateralSymbol?: string
  collateralDecimals?: number
  collateralAsset?: Address
  collateralLogoURI?: string
  collateralBalance?: string
  collateralShares?: string
  // New: array of collateral options
  collateralOptions?: CollateralOption[]
  subAccountId?: number
}

enum RepayDialogTabs {
  FromWallet = 'From Wallet',
  WithCollateral = 'With Collateral',
}

type RepaySource = 'wallet' | 'collateral'

function EBorrowUnifiedRepayDialogContent({
  closeDialog,
  liabilityVault,
  liabilitySymbol,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  maxRepayAmount,
  collateralVault,
  collateralSymbol,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  collateralBalance,
  collateralShares,
  collateralOptions,
  subAccountId,
}: Omit<EBorrowUnifiedRepayDialogProps, 'open' | 'setOpen'> & { closeDialog: () => void }) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<RepayDialogTabs>(RepayDialogTabs.FromWallet)
  const [isSuccess, setIsSuccess] = useState(false)

  // Build collateral options array (backwards compatible with single collateral props)
  const allCollateralOptions = useMemo((): CollateralOption[] => {
    if (collateralOptions && collateralOptions.length > 0) {
      return collateralOptions
    }
    // Backwards compatibility: convert single collateral props to array
    if (
      collateralVault &&
      collateralSymbol &&
      collateralDecimals !== undefined &&
      collateralAsset &&
      collateralBalance
    ) {
      return [
        {
          vaultAddress: collateralVault,
          symbol: collateralSymbol,
          decimals: collateralDecimals,
          assetAddress: collateralAsset,
          logoURI: collateralLogoURI,
          balance: collateralBalance,
          shares: collateralShares,
        },
      ]
    }
    return []
  }, [
    collateralOptions,
    collateralVault,
    collateralSymbol,
    collateralDecimals,
    collateralAsset,
    collateralLogoURI,
    collateralBalance,
    collateralShares,
  ])

  // Get first collateral as default selection (view handles its own selection state)
  const selectedCollateral = allCollateralOptions[0]

  // Handler that invalidates queries and sets success state
  const handleSuccess = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['readContract'], refetchType: 'all' })
    void queryClient.invalidateQueries({ queryKey: ['readContracts'], refetchType: 'all' })
    setIsSuccess(true)
  }, [queryClient])

  // Check if user has any collateral to enable that option
  const hasCollateral = allCollateralOptions.some((c) => Number(c.balance) > 0)

  // Determine which tabs to show
  const availableTabs = hasCollateral ? Object.values(RepayDialogTabs) : [RepayDialogTabs.FromWallet]

  // Reset activeTab to "From Wallet" if current tab is no longer available
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(RepayDialogTabs.FromWallet)
    }
  }, [availableTabs, activeTab])

  // Map tab to repay source
  const repaySource: RepaySource = activeTab === RepayDialogTabs.FromWallet ? 'wallet' : 'collateral'

  return (
    <>
      {/* Only show title and tabs when not in success state */}
      {!isSuccess && (
        <>
          <DialogTitle className="mb-4 font-semibold text-white text-xl">Repay {liabilitySymbol}</DialogTitle>

          <TabSelector
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={availableTabs}
            className="mt-2 mb-6 pb-2"
          />
        </>
      )}

      {repaySource === 'wallet' && (
        <EvcRepayDialogContent
          closeDialog={closeDialog}
          liabilityVault={liabilityVault}
          liabilitySymbol={liabilitySymbol}
          liabilityDecimals={liabilityDecimals}
          liabilityAsset={liabilityAsset}
          liabilityLogoURI={liabilityLogoURI}
          maxRepayAmount={maxRepayAmount}
          subAccountId={subAccountId}
          onSuccess={handleSuccess}
        />
      )}

      {repaySource === 'collateral' && selectedCollateral && (
        <EBorrowRepayWithCollateralView
          closeDialog={closeDialog}
          collateralVault={selectedCollateral.vaultAddress}
          collateralSymbol={selectedCollateral.symbol}
          collateralDecimals={selectedCollateral.decimals}
          collateralAsset={selectedCollateral.assetAddress}
          collateralLogoURI={selectedCollateral.logoURI}
          collateralBalance={selectedCollateral.balance}
          collateralShares={selectedCollateral.shares}
          collateralOptions={allCollateralOptions.length > 1 ? allCollateralOptions : undefined}
          liabilityVault={liabilityVault}
          liabilitySymbol={liabilitySymbol}
          liabilityDecimals={liabilityDecimals}
          liabilityAsset={liabilityAsset}
          liabilityLogoURI={liabilityLogoURI}
          debtOwed={maxRepayAmount}
          subAccountId={subAccountId}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

const EBorrowUnifiedRepayDialogContentWithSuspense = withSuspense(
  EBorrowUnifiedRepayDialogContent,
  DialogContentSkeleton,
)

function EBorrowUnifiedRepayDialog({ open, setOpen, ...props }: EBorrowUnifiedRepayDialogProps) {
  const isWrongNetwork = useWrongNetwork()
  if (isWrongNetwork) {
    setOpen(false)
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <EBorrowUnifiedRepayDialogContentWithSuspense closeDialog={() => setOpen(false)} {...props} />
      </DialogContent>
    </Dialog>
  )
}

export const eBorrowUnifiedRepayDialogConfig: DialogConfig<EBorrowUnifiedRepayDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: EBorrowUnifiedRepayDialog,
}
